import React, { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { KeyRound, ArrowLeft, CheckCircle2, XCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Renders when a user follows the reset link from their email. Reads the
// `?token=...` param and posts a new password. On success we bounce them
// back to /login with a toast-like banner.

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const clientError = useMemo(() => {
    if (!password) return "";
    if (password.length < 8) return "Password must be at least 8 characters.";
    if (confirm && confirm !== password) return "The two passwords don't match.";
    return "";
  }, [password, confirm]);

  const submit = async (e) => {
    e.preventDefault();
    if (clientError) { setError(clientError); return; }
    setBusy(true);
    setError("");
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, { token, password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (err.message || "Something went wrong."));
    } finally {
      setBusy(false);
    }
  };

  const tokenMissing = !token;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-secondary/30 to-background px-4 py-10">
      <div className="w-full max-w-md" data-testid="reset-password-page">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="reset-back-to-login">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
        <div className="text-center mb-6">
          <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-3">
            <KeyRound className="h-5 w-5" />
          </div>
          <h1 className="text-2xl font-display font-bold tracking-tight">Choose a new password</h1>
          <p className="text-sm text-muted-foreground mt-1.5">Your new password will be used the next time you sign in.</p>
        </div>

        {tokenMissing ? (
          <Card className="p-6 space-y-3 text-center" data-testid="reset-password-missing-token">
            <div className="mx-auto h-10 w-10 rounded-full bg-destructive/10 text-destructive flex items-center justify-center">
              <XCircle className="h-5 w-5" />
            </div>
            <h2 className="text-lg font-semibold">This reset link is incomplete</h2>
            <p className="text-sm text-muted-foreground">
              The link you followed is missing its security token. Request a new reset from the sign-in screen.
            </p>
            <Button asChild variant="outline">
              <Link to="/forgot-password">Request a new link</Link>
            </Button>
          </Card>
        ) : done ? (
          <Card className="p-6 space-y-3 text-center" data-testid="reset-password-success">
            <div className="mx-auto h-10 w-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <h2 className="text-lg font-semibold">Password updated</h2>
            <p className="text-sm text-muted-foreground">Redirecting you to the sign-in screen…</p>
            <Button asChild>
              <Link to="/login">Sign in now</Link>
            </Button>
          </Card>
        ) : (
          <Card className="p-6">
            <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
              <div>
                <Label htmlFor="rp-password">New password</Label>
                <Input
                  id="rp-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  data-testid="reset-password-input"
                />
              </div>
              <div>
                <Label htmlFor="rp-confirm">Confirm password</Label>
                <Input
                  id="rp-confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-password-confirm"
                />
              </div>
              {(error || clientError) && (
                <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2" data-testid="reset-password-error">
                  {error || clientError}
                </div>
              )}
              <Button
                type="submit"
                disabled={busy || !password || !!clientError}
                className="w-full h-11 rounded-full"
                data-testid="reset-password-submit"
              >
                {busy ? "Saving…" : "Set new password"}
              </Button>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
}
